import React from 'react';
import { theme } from '../theme';

// Public marketing-style legal pages. Mounted at /privacy and /terms by
// LandingPage routing so Google Play + App Store review can link to them.

const Shell = ({ title, children }) => (
  <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#111', lineHeight: 1.6 }}>
    <a href="/" style={{ fontSize: 14, color: theme.primary, textDecoration: 'none' }}>&larr; Back to ORALZY</a>
    <h1 style={{ fontSize: 32, marginTop: 20, marginBottom: 10 }}>{title}</h1>
    <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 30 }}>Last updated: 17 April 2026</p>
    {children}
    <footer style={{ marginTop: 60, paddingTop: 20, borderTop: '1px solid #eee', fontSize: 13, color: '#6b7280' }}>
      Questions? Write to <a href="mailto:support@oralzy.com" style={{ color: theme.primary }}>support@oralzy.com</a>
    </footer>
  </div>
);

export const PrivacyPage = () => (
  <Shell title="Privacy Policy">
    <h2>Who we are</h2>
    <p>ORALZY (oralzy.com) is a multi-tenant software-as-a-service platform for dental clinics operating in India. This policy explains how we collect and process personal data under the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>.</p>

    <h2>What we collect</h2>
    <ul>
      <li><strong>Staff accounts:</strong> name, email, phone, clinic role, device fingerprint for login.</li>
      <li><strong>Patients (entered by the clinic):</strong> name, phone, age, address, UHID, medical history, dental records, X-rays, prescriptions, bills, consent forms.</li>
      <li><strong>Patient portal (OTP login):</strong> phone, one-time OTP hash, session token, push-notification token.</li>
      <li><strong>Technical:</strong> IP, browser/app version, audit logs for security.</li>
    </ul>

    <h2>Why we collect it</h2>
    <ul>
      <li>Operate the clinic platform (appointments, billing, prescriptions).</li>
      <li>Send appointment reminders and treatment follow-ups via SMS / WhatsApp / push.</li>
      <li>Comply with medical record-keeping obligations (typically 3–10 years under state dental council rules).</li>
      <li>Prevent fraud, secure accounts, and debug crashes.</li>
    </ul>

    <h2>Where data lives</h2>
    <p>All data is stored on MongoDB Atlas clusters in the <strong>Mumbai (ap-south-1)</strong> region, per DPDP Act localisation expectations. Nightly backups are encrypted and retained for 30 days.</p>

    <h2>Who sees what</h2>
    <p>Each dental clinic is isolated in its own tenant. Clinic staff only see their own clinic's patients. ORALZY platform engineers can access raw data only for documented support tickets and incident response.</p>

    <h2>Third-party processors</h2>
    <ul>
      <li><strong>MongoDB Atlas</strong> — database hosting (Mumbai region).</li>
      <li><strong>Render</strong> — application runtime (Singapore; no patient data in logs).</li>
      <li><strong>Razorpay</strong> — optional payment processing when a clinic enables online bills.</li>
      <li><strong>Twilio / 2Factor.in</strong> — SMS + OTP delivery.</li>
      <li><strong>Meta WhatsApp Cloud API</strong> — appointment reminders where the clinic enables it.</li>
      <li><strong>Google Gemini</strong> — Veeransh AI assistant (redacted prompt data; no raw patient identifiers).</li>
      <li><strong>Expo push service</strong> — mobile notifications.</li>
    </ul>

    <h2>Your rights (DPDP §11–13)</h2>
    <ul>
      <li><strong>Access</strong> — request a copy of your data.</li>
      <li><strong>Correction / erasure</strong> — ask us to fix or delete personal data.</li>
      <li><strong>Consent withdrawal</strong> — turn off SMS / WhatsApp / push anytime in the portal.</li>
      <li><strong>Grievance</strong> — our Data Protection Officer: Dr. Sumit (<a href="mailto:dpo@oralzy.com">dpo@oralzy.com</a>), response within 30 days.</li>
    </ul>

    <h2>Retention</h2>
    <p>Medical records are retained for the period mandated by your state dental council (default 10 years from last visit). Account audit logs: 2 years. Backups: 30 days. Patient portal session tokens: 24 hours.</p>

    <h2>Security</h2>
    <p>TLS in transit, bcrypt for passwords and OTPs, per-user TOTP for staff, tenant-scoped data access, daily encrypted backups, monthly backup-restore verification.</p>

    <h2>Children</h2>
    <p>Parents/guardians add minor children under their own patient record. We do not knowingly target children directly.</p>

    <h2>Changes</h2>
    <p>Material changes will be notified at least 14 days in advance via the app and email.</p>
  </Shell>
);

export const TermsPage = () => (
  <Shell title="Terms of Service">
    <h2>1. Who can use ORALZY</h2>
    <p>Registered dental practitioners (BDS/MDS), their staff, and patients invited by those clinics. You must be at least 18 to create a clinic account.</p>

    <h2>2. Subscription & payment</h2>
    <p>ORALZY is offered in Starter / Pro / Enterprise tiers. Fees are billed monthly or annually; trial periods and promotional discounts are non-transferable. Fees are exclusive of GST. Non-payment after 14 days may result in read-only downgrade and eventual account suspension.</p>

    <h2>3. Clinical responsibility</h2>
    <p>ORALZY is a record-keeping and decision-support tool. Clinical judgments, prescriptions, diagnoses, and treatment decisions remain the sole responsibility of the treating doctor. Veeransh AI suggestions are informational and do not replace clinical assessment.</p>

    <h2>4. Data ownership</h2>
    <p>Each clinic owns the data it enters. ORALZY acts as a data processor. On account closure you may export your data in JSON/PDF within 30 days; we permanently delete it after 60 days.</p>

    <h2>5. Acceptable use</h2>
    <ul>
      <li>No sharing of login credentials across doctors.</li>
      <li>No uploading of data you do not have consent to process.</li>
      <li>No scraping or reverse-engineering.</li>
      <li>Patient portal is for the patient's own records only.</li>
    </ul>

    <h2>6. Availability</h2>
    <p>We target 99.5% monthly uptime excluding scheduled maintenance and third-party outages (MongoDB Atlas, Render, payment gateways). No warranty is given beyond this target.</p>

    <h2>7. Limitation of liability</h2>
    <p>To the maximum extent permitted by law, ORALZY's aggregate liability in any 12-month period is limited to the fees paid by the affected clinic in that period. We are not liable for indirect, consequential, or loss-of-revenue claims.</p>

    <h2>8. Indemnity</h2>
    <p>You indemnify ORALZY against claims arising from your misuse of the service or breach of patient consent.</p>

    <h2>9. Termination</h2>
    <p>Either party may terminate for material breach after 14 days' written notice and opportunity to cure.</p>

    <h2>10. Governing law</h2>
    <p>These terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at New Delhi.</p>
  </Shell>
);
